// The order the seller works their own queue in (YCRM-40).
//
// This only reorders leads the seller already holds. Which leads they get stays
// with the server's random allocation; nothing here reaches it.

import { BAND } from '@/shared/domain'
import type { LeadRow } from '@/shared/types'
import { compareBySort } from '@/features/leads/table/columns'
import { queueBandOf } from '@/features/leads/workflow/queue'

/** 'standard' is the banded, promise-first order; the other two rank by AI
 *  score or lead date, newest and highest first, missing values last. */
export type FocusSort = 'standard' | 'score' | 'date'

export function isValidFocusSort(v: unknown): v is FocusSort {
  return v === 'standard' || v === 'score' || v === 'date'
}

export const FOCUS_SORT_LABEL: Record<FocusSort, string> = {
  standard: 'Standard',
  score: 'Poäng',
  date: 'Datum',
}

export function compareFocusQueue(a: LeadRow, b: LeadRow, now: number, sort: FocusSort): number {
  // Not-skipped first in every order, so a seller who shows everything still
  // works the active queue before revisiting what they set aside.
  const skipDelta = (a.skipped ? 1 : 0) - (b.skipped ? 1 : 0)
  if (skipDelta) return skipDelta

  if (sort !== 'standard') {
    const d = compareBySort(a, b, { key: sort === 'score' ? 'score' : 'leadDate', dir: 'desc' })
    if (d) return d
    // A tie falls through to the standard order, so equal scores or dates keep
    // the place they had and no row jumps between refetches.
  }

  const ba = queueBandOf(a, now)
  const bb = queueBandOf(b, now)
  if (ba !== bb) return ba - bb

  // Inside "Call now", a committed callback beats a routine follow-up. This
  // matters because Reached (+7d), No answer (+1d) and Email sent (+7d) all
  // backfill a date silently — without this the band becomes "everything
  // you have ever touched, oldest first" and a real appointment sinks.
  if (ba === BAND.DUE) {
    const alertDelta = (a.isCallbackAlert ? 0 : 1) - (b.isCallbackAlert ? 0 : 1)
    if (alertDelta) return alertDelta
  }
  if (ba !== BAND.FRESH) {
    const aDate = a.nextContactAt ? Date.parse(a.nextContactAt) : NaN
    const bDate = b.nextContactAt ? Date.parse(b.nextContactAt) : NaN
    if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
      return aDate - bDate
    }
  }
  // Nothing ranks one lead above another inside a band any more, so the
  // id is the whole of the remaining order. Stable across refetches, which
  // the queue navigator's position and prev/next both assume.
  return a.id.localeCompare(b.id)
}
